/* eslint-disable max-len */
/* eslint-disable consistent-return */
import { Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import KidneyRisk from './KidneyRisk';

import { useAppSelector } from 'app/store';
import Icon from 'common/components/Icon';
import SummaryCard from 'common/components/SummaryCard';
import {
  getAgeByKidneyRiskV3Service,
  getAtherosclerosisByKidneyRiskV3Service,
  getCounterKidneyRiskTwentyPercentV3Service,
  getCounterResultV3Service,
  getKioskKidneyRiskV3Service,
  getYTNCForOneByKidneyRiskV3Service
} from 'common/services/dashboard';
import { KidneyRiskCode } from 'common/services/dashboard/types';
import { defaultDateRangeYear } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import HeaderKiosk from 'extends/HeaderKiosk';
import KioskDashboardFilter from 'extends/KioskDashboardFilter';
import KioskHeartRiskBySickType from 'extends/KioskHeartRiskBySickType';
import KioskKidneyRenalByRisk from 'extends/KioskKidneyRenalByRisk';
import KioskRenalRiskByAge from 'extends/KioskRenalRiskByAge';

const RiskRenalManagement = () => {
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const { t } = useTranslation();
  const [typeParam, setTypeParam] = useState<number | undefined>(undefined);
  const [kiosGroupId, setKiosGroupId] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<string[]>(defaultDateRangeYear);
  const [isErrorTimeoutKidney, setIsErrorTimeoutKidney] = useState<boolean>(false);

  const {
    data: kiosKidneyData, refetch: refetchKidney, isFetching: isFetchingKidney
  } = useQuery(
    ['getKioskKidneyRiskV3Query3'],
    () => getKioskKidneyRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeoutKidney(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeoutKidney(false);
      }
    }
  );

  const {
    data: kiosCounterData, refetch: refetchCounter
  } = useQuery(
    ['getCounterResultV3Query3-renal'],
    () => getCounterResultV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    })
  );

  const {
    data: counterKidneyRiskTwentyPercentData, refetch: refetchKidneyRiskTwentyPercent
  } = useQuery(
    ['getCounterKidneyRiskTwentyPercentV3Query3-renal'],
    () => getCounterKidneyRiskTwentyPercentV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: ageByKidneyRisk,
    refetch: refetchAgeByKidneyRisk,
    isLoading: isLoadingAgeByKidneyRisk
  } = useQuery(
    ['getAgeByKidneyRiskV3Query3'],
    () => getAgeByKidneyRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeoutKidney(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeoutKidney(false);
      }
    }
  );

  const {
    data: atherosclerosisByKidneyRisk,
    refetch: refetchAtherosclerosisByKidneyRisk,
    isLoading: isLoadingAtherosclerosisByKidneyRisk
  } = useQuery(
    ['getAtherosclerosisByKidneyRiskV3Query3'],
    () => getAtherosclerosisByKidneyRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeoutKidney(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeoutKidney(false);
      }
    }
  );

  const {
    data: YTNCForOneByKidneyRisk,
    refetch: refetchYTNCForOneByKidneyRisk,
    isLoading: isLoadingYTNCForOneByKidneyRisk
  } = useQuery(
    ['getYTNCForOneByKidneyRiskV3Query3'],
    () => getYTNCForOneByKidneyRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeoutKidney(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeoutKidney(false);
      }
    }
  );

  const handleClear = () => {
    setDateRange([]);
    setTypeParam(undefined);
    setKiosGroupId([]);
  };

  const { transformedAgeByKidneyRisk, ageByKidneyRiskRawData } = useMemo(() => {
    let dataResult;
    let rawDataResult: number[] = [];

    if (ageByKidneyRisk) {
      const totalLess40 = ageByKidneyRisk.reduce((acc, cur) => acc + cur.less40, 0);
      const totalBetween40And50 = ageByKidneyRisk.reduce((acc, cur) => acc + cur.between40And50, 0);
      const totalBetween50And69 = ageByKidneyRisk.reduce((acc, cur) => acc + cur.between50And69, 0);
      const totalOver70 = ageByKidneyRisk.reduce((acc, cur) => acc + cur.over70, 0);

      dataResult = {
        datasets: [
          {
            data: [
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.less40 || 0) * 100 / totalLess40).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.between40And50 || 0) * 100 / totalBetween40And50).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.between50And69 || 0) * 100 / totalBetween50And69).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.over70 || 0) * 100 / totalOver70).toFixed(0),
            ]
          },
          {
            data: [
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.less40 || 0) * 100 / totalLess40).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.between40And50 || 0) * 100 / totalBetween40And50).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.between50And69 || 0) * 100 / totalBetween50And69).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.over70 || 0) * 100 / totalOver70).toFixed(0),
            ]
          },
          {
            data: [
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.less40 || 0) * 100 / totalLess40).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.between40And50 || 0) * 100 / totalBetween40And50).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.between50And69 || 0) * 100 / totalBetween50And69).toFixed(0),
              ((ageByKidneyRisk.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.over70 || 0) * 100 / totalOver70).toFixed(0),
            ]
          }
        ]
      };

      rawDataResult = [
        totalLess40,
        totalBetween40And50,
        totalBetween50And69,
        totalOver70
      ];
    }

    return {
      transformedAgeByKidneyRisk: dataResult,
      ageByKidneyRiskRawData: rawDataResult
    };
  }, [ageByKidneyRisk]);

  const { transformedAtherosclerosisByKidneyRisk } = useMemo(() => {
    let dataResult;

    if (atherosclerosisByKidneyRisk) {
      const totalDotquyThieumau = atherosclerosisByKidneyRisk.dotquyThieumau.reduce((acc, cur) => acc + cur.total, 0);
      const totalNhoimaucotim = atherosclerosisByKidneyRisk.nhoimaucotim.reduce((acc, cur) => acc + cur.total, 0);
      const totalDatstent = atherosclerosisByKidneyRisk.datstent.reduce((acc, cur) => acc + cur.total, 0);
      const totalDauthatnguc = atherosclerosisByKidneyRisk.dauthatnguc.reduce((acc, cur) => acc + cur.total, 0);
      const totalPhinhtachdongmach = atherosclerosisByKidneyRisk.phinhtachdongmach.reduce((acc, cur) => acc + cur.total, 0);

      dataResult = {
        datasets: [
          {
            data: [
              ((atherosclerosisByKidneyRisk.dotquyThieumau.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalDotquyThieumau).toFixed(0),
              ((atherosclerosisByKidneyRisk.nhoimaucotim.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalNhoimaucotim).toFixed(0),
              ((atherosclerosisByKidneyRisk.datstent.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalDatstent).toFixed(0),
              ((atherosclerosisByKidneyRisk.dauthatnguc.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalDauthatnguc).toFixed(0),
              ((atherosclerosisByKidneyRisk.phinhtachdongmach.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalPhinhtachdongmach).toFixed(0),
            ]
          },
          {
            data: [
              ((atherosclerosisByKidneyRisk.dotquyThieumau.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalDotquyThieumau).toFixed(0),
              ((atherosclerosisByKidneyRisk.nhoimaucotim.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalNhoimaucotim).toFixed(0),
              ((atherosclerosisByKidneyRisk.datstent.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalDatstent).toFixed(0),
              ((atherosclerosisByKidneyRisk.dauthatnguc.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalDauthatnguc).toFixed(0),
              ((atherosclerosisByKidneyRisk.phinhtachdongmach.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalPhinhtachdongmach).toFixed(0),
            ]
          },
          {
            data: [
              ((atherosclerosisByKidneyRisk.dotquyThieumau.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalDotquyThieumau).toFixed(0),
              ((atherosclerosisByKidneyRisk.nhoimaucotim.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalNhoimaucotim).toFixed(0),
              ((atherosclerosisByKidneyRisk.datstent.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalDatstent).toFixed(0),
              ((atherosclerosisByKidneyRisk.dauthatnguc.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalDauthatnguc).toFixed(0),
              ((atherosclerosisByKidneyRisk.phinhtachdongmach.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalPhinhtachdongmach).toFixed(0),
            ]
          },
        ]
      };
    }

    return {
      transformedAtherosclerosisByKidneyRisk: dataResult,
    };
  }, [atherosclerosisByKidneyRisk]);

  const { transformedYTNCForOneByKidneyRisk, YTNCForOneByKidneyRiskRawData } = useMemo(() => {
    let dataResult;
    let rawDataResult: number[] = [];

    if (YTNCForOneByKidneyRisk) {
      const totalDTD = YTNCForOneByKidneyRisk.ytncDTD.reduce((acc, curr) => acc + curr.total, 0);
      const totalTHA = YTNCForOneByKidneyRisk.ytncTHA.reduce((acc, curr) => acc + curr.total, 0);
      const totalRLLP = YTNCForOneByKidneyRisk.ytncRLLP.reduce((acc, curr) => acc + curr.total, 0);

      dataResult = {
        datasets: [
          {
            data: [
              ((YTNCForOneByKidneyRisk.ytncDTD.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalDTD).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncTHA.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalTHA).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncRLLP.find((it) => it.kidneyRiskCode === KidneyRiskCode.hasCDK)?.total || 0) * 100 / totalRLLP).toFixed(0),
            ]
          },
          {
            data: [
              ((YTNCForOneByKidneyRisk.ytncDTD.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalDTD).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncTHA.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalTHA).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncRLLP.find((it) => it.kidneyRiskCode === KidneyRiskCode.inDangerOfCDK)?.total || 0) * 100 / totalRLLP).toFixed(0),
            ]
          },
          {
            data: [
              ((YTNCForOneByKidneyRisk.ytncDTD.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalDTD).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncTHA.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalTHA).toFixed(0),
              ((YTNCForOneByKidneyRisk.ytncRLLP.find((it) => it.kidneyRiskCode === KidneyRiskCode.noneCDK)?.total || 0) * 100 / totalRLLP).toFixed(0),
            ]
          }
        ]
      };

      rawDataResult = [
        totalDTD,
        totalTHA,
        totalRLLP
      ];
    }

    return {
      transformedYTNCForOneByKidneyRisk: dataResult,
      YTNCForOneByKidneyRiskRawData: rawDataResult
    };
  }, [YTNCForOneByKidneyRisk]);

  return (
    <div className="riskRenalManagement">
      <HeaderKiosk
        icon={<Icon iconName="kidney" />}
        title={t('dashboard2025.renalRisk2025')}
        bgColor="blue"
      />
      <div className="riskRenalManagement-wrapper">
        <KioskDashboardFilter
          typeParam={typeParam}
          setTypeParam={setTypeParam}
          kiosGroupId={kiosGroupId}
          setKiosGroupId={setKiosGroupId}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onClear={handleClear}
          onRefetch={refetchCounter}
          onRefetch2={refetchKidneyRiskTwentyPercent}
          onRefetch3={refetchKidney}
          onRefetch4={refetchAgeByKidneyRisk}
          onRefetch5={refetchAtherosclerosisByKidneyRisk}
          onRefetch6={refetchYTNCForOneByKidneyRisk}
          isBlue
        />
        <div className="riskRenalManagement-card">
          <SummaryCard
            title={t('dashboard2025.total')}
            number={kiosCounterData?.total || 0}
          />
          <SummaryCard
            title={t('dashboard2025.kidneyTotal')}
            number={counterKidneyRiskTwentyPercentData?.total || 0}
          />
        </div>
        <Spin spinning={isFetchingKidney || isLoadingAtherosclerosisByKidneyRisk || isLoadingAgeByKidneyRisk || isLoadingYTNCForOneByKidneyRisk}>
          <div className="riskRenalManagement-charts">
            {
              isErrorTimeoutKidney ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                  }}
                >
                  <Typography.Title level={4}>
                    {t('message.errorTimeout')}
                  </Typography.Title>
                </div>
              ) : (
                <>
                  {getPermission(rolesUser, roles.REPORT_KIDNEY_RISK_V3) && (
                    <KidneyRisk
                      kidneyRisk={{
                        zeroPercent: kiosKidneyData?.find((item) => item.cdk === 'zeroPercent')?.total || 0,
                        missing: kiosKidneyData?.find((item) => item.cdk === 'missing')?.total || 0,
                        stage_3: kiosKidneyData?.find((item) => item.cdk === 'stage_3')?.total || 0,
                        stage_4_5: kiosKidneyData?.find((item) => item.cdk === 'stage_4_5')?.total || 0,
                        twentyPercent: kiosKidneyData?.find((item) => item.cdk === 'twentyPercent')?.total || 0,
                      }}
                      style={{
                        minHeight: 700,
                        justifyContent: 'space-between'
                      }}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_ATHEROSCLEROSIS_BY_KIDNEY_RISK) && (
                    <KioskHeartRiskBySickType
                      title={t('dashboard2025.heartRiskBySickType')}
                      data={transformedAtherosclerosisByKidneyRisk}
                      style={{ minHeight: 700 }}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_AGE_BY_KIDNEY_RISK) && (
                    <KioskRenalRiskByAge
                      title={t('dashboard2025.renalRiskByAge')}
                      data={transformedAgeByKidneyRisk}
                      style={{ minHeight: 700 }}
                      dataOnTop={ageByKidneyRiskRawData}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_YTNC_FOR_ONE_BY_KIDNEY_RISK) && (
                    <KioskKidneyRenalByRisk
                      title={t('dashboard2025.YTNCForOneByKidneyRisk')}
                      data={transformedYTNCForOneByKidneyRisk}
                      dataOnTop={YTNCForOneByKidneyRiskRawData}
                      style={{ minHeight: 700 }}
                    />
                  )}
                </>
              )
            }
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default RiskRenalManagement;
